import { configAt } from '@vagusx/agent-core/v2/config-path';
import { useEffect, useMemo } from 'react';
import {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useStore,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  CalendarDays,
  ClipboardList,
  Flag,
  History,
  MessageSquare,
  PhoneIncoming,
  PhoneOff,
  Plus,
  Share2,
  ShieldCheck,
  Split,
  TriangleAlert,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type {
  AgentComponent,
  CallFact,
  ComponentCategory,
  DecisionPreset,
  WorkflowGraph,
  WorkflowStep,
} from '@vagusx/clients';
import { cn } from '@/lib/utils';
import {
  allExits,
  branchLabel,
  decisionOf,
  endingExits,
  nextOf,
  otherwiseLabel,
  previewLine,
  stepExits,
  unreachable,
  type NodeProblems,
} from './graph';
import { layoutGraph, STEP_HEIGHT, STEP_WIDTH, type LayoutEdge, type Point } from './canvas-layout';

/**
 * The workflow as a picture: steps as boxes, arrows labelled with the ending
 * that takes them (ADR-276).
 *
 * BUILT FOR PEOPLE WHO DO NOT THINK IN GRAPHS. Boxes are laid out, not
 * dragged, so the picture always reads top to bottom in the order a call
 * walks it. There are two ways to change it, both on the canvas itself: the
 * + on any arrow adds a step there, and dragging from the dot under a box to
 * another box draws an arrow (the editor asks which ending it is for). Where
 * each ending goes is also set in words in the step's panel. What a caller
 * can do during any step is shown as a badge on the step that answers it,
 * never as arrows from every box.
 */

/** Shown above a step whose name is its component's own title. */
const CATEGORY_LABELS: Record<ComponentCategory, string> = {
  open: 'Start',
  identity: 'Identity',
  collect: 'Details',
  schedule: 'Appointments',
  inform: 'Tell them',
  handoff: 'Hand off',
  decide: 'Decision',
  close: 'Wrap up',
};

const CATEGORY_ICONS: Record<ComponentCategory, LucideIcon> = {
  open: PhoneIncoming,
  identity: ShieldCheck,
  collect: ClipboardList,
  schedule: CalendarDays,
  inform: MessageSquare,
  handoff: Users,
  decide: Split,
  close: Flag,
};

export interface CanvasProps {
  graph: WorkflowGraph;
  steps: ReadonlyMap<string, WorkflowStep>;
  components: ReadonlyMap<string, AgentComponent>;
  presets: readonly DecisionPreset[];
  /** For showing a line with example values in place of its placeholders. */
  callFacts: readonly CallFact[];
  problems: ReadonlyMap<string, NodeProblems>;
  selectedId: string | null;
  busy: boolean;
  /** Changes when the view should re-centre on the start (a different workflow). */
  fitKey: string;
  onSelect: (nodeId: string | null) => void;
  /** The + on the arrow from `from` to `to`. */
  onInsert: (from: string, to: string) => void;
  /** An arrow dragged from `from` to `to`. */
  onConnect: (from: string, to: string) => void;
}

export function Canvas(props: CanvasProps): React.ReactElement {
  return (
    <div className="workflow-canvas h-full w-full">
      <ReactFlowProvider>
        <Flow {...props} />
      </ReactFlowProvider>
    </div>
  );
}

interface StepData extends Record<string, unknown> {
  name: string;
  kind: string;
  category: ComponentCategory | null;
  detail: string;
  isEntry: boolean;
  shared: boolean;
  endsCall: boolean;
  /** Endings that end the call here on purpose (a transfer). */
  endsOn: string[];
  anyTime: string[];
  legacyGates: boolean;
  errors: number;
  warnings: number;
  orphan: boolean;
  selected: boolean;
}

interface ArrowData extends Record<string, unknown> {
  path: string;
  labelAt: Point;
  label: string;
  busy: boolean;
  onInsert: () => void;
  onPick: () => void;
}

type StepNode = Node<StepData, 'step'>;
type ArrowEdge = Edge<ArrowData, 'arrow'>;

const nodeTypes = { step: StepBox };
const edgeTypes = { arrow: Arrow };

function Flow({
  graph,
  steps,
  components,
  presets,
  callFacts,
  problems,
  selectedId,
  busy,
  fitKey,
  onSelect,
  onInsert,
  onConnect,
}: CanvasProps): React.ReactElement {
  const flow = useReactFlow();
  const width = useStore((s) => s.width);

  const layoutEdges = useMemo((): LayoutEdge[] => {
    const out: LayoutEdge[] = [];
    for (const node of graph.nodes) {
      const step = steps.get(node.stepId);
      const component = components.get(step?.componentKey ?? '');
      const exits = stepExits(step, component, presets);
      const labels = allExits(step, component, presets);
      const next = nextOf(node);
      const byTarget = new Map<string, { labels: string[]; isDefault: boolean }>();
      for (const b of next.branches) {
        const entry = byTarget.get(b.to) ?? { labels: [], isDefault: false };
        entry.labels.push(branchLabel(b, labels));
        byTarget.set(b.to, entry);
      }
      if (next.otherwise) {
        const entry = byTarget.get(next.otherwise) ?? { labels: [], isDefault: false };
        entry.isDefault = true;
        byTarget.set(next.otherwise, entry);
      }
      for (const [to, entry] of byTarget) {
        if (!graph.nodes.some((n) => n.id === to)) continue;
        out.push({
          id: `${node.id}->${to}`,
          source: node.id,
          target: to,
          label: entry.isDefault ? otherwiseLabel(node, exits) : entry.labels.join(' or '),
        });
      }
    }
    return out;
  }, [graph, steps, components, presets]);

  const layout = useMemo(
    () =>
      layoutGraph(
        graph.nodes.map((n) => n.id),
        layoutEdges
      ),
    [graph.nodes, layoutEdges]
  );

  const orphans = useMemo(() => unreachable(graph), [graph]);
  const anyTimeFor = useMemo(() => {
    const m = new Map<string, string[]>();
    const add = (id: string | undefined, label: string): void => {
      if (id) m.set(id, [...(m.get(id) ?? []), label]);
    };
    add(graph.anyTime?.handoff, 'asks for a person');
    add(graph.anyTime?.callback, 'asks for a callback');
    add(graph.close, 'wants to end the call');
    return m;
  }, [graph]);

  const examples = useMemo(() => new Map(callFacts.map((f) => [f.path, f.example])), [callFacts]);

  // Every box is the same size, and saying so up front keeps React Flow from
  // re-measuring each render — until it has, it draws no arrows at all.
  const nodes = useMemo(
    () =>
      graph.nodes.map((node): StepNode => {
        const step = steps.get(node.stepId);
        const component = components.get(step?.componentKey ?? '');
        const category = component?.meta.category ?? null;
        const title = component?.meta.title ?? step?.componentKey ?? node.stepId;
        const name = step?.name ?? 'Missing step';
        const preset =
          component?.key === 'Decide' && typeof step?.config.preset === 'string'
            ? presets.find((p) => p.key === step.config.preset)
            : undefined;
        const decision =
          component?.key === 'Decide' && step ? decisionOf(step.config, presets) : null;
        const line = firstLine(step, component);
        return {
          id: node.id,
          type: 'step',
          position: layout.positions.get(node.id) ?? { x: 0, y: 0 },
          width: STEP_WIDTH,
          height: STEP_HEIGHT,
          measured: { width: STEP_WIDTH, height: STEP_HEIGHT },
          draggable: false,
          data: {
            name,
            kind: name === title && category ? CATEGORY_LABELS[category] : title,
            category,
            detail: decision
              ? (preset?.hint ?? decision.answers.map((a) => a.label).join(' / '))
              : line
                ? `“${previewLine(line, examples)}”`
                : (component?.meta.summary ?? ''),
            isEntry: node.id === graph.entry,
            shared: step?.ownerWorkflowId === null,
            endsCall: node.transitions.length === 0,
            endsOn: endingExits(node, stepExits(step, component, presets)).map((e) => e.label),
            anyTime: anyTimeFor.get(node.id) ?? [],
            legacyGates: Boolean(node.requires?.length || node.unless?.length),
            errors: problems.get(node.id)?.errors.length ?? 0,
            warnings: problems.get(node.id)?.warnings.length ?? 0,
            orphan: orphans.has(node.id),
            selected: node.id === selectedId,
          },
        };
      }),
    [graph, steps, components, presets, layout, examples, anyTimeFor, problems, orphans, selectedId]
  );

  const edges = layout.edges.map((e): ArrowEdge => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: 'arrow',
    markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
    data: {
      path: e.path,
      labelAt: e.labelAt,
      label: e.label,
      busy,
      onInsert: () => onInsert(e.source, e.target),
      onPick: () => onSelect(e.source),
    },
  }));

  // Open at a readable zoom with the start of the call at the top, rather than
  // shrinking the whole workflow to fit.
  useEffect(() => {
    if (!width || layout.positions.size === 0) return;
    const xs = [...layout.positions.values()].map((p) => p.x);
    const ys = [...layout.positions.values()].map((p) => p.y);
    const left = Math.min(...xs);
    const right = Math.max(...xs) + STEP_WIDTH;
    const zoom = Math.min(1, Math.max(0.6, (width - 48) / (right - left)));
    void flow.setViewport(
      { x: width / 2 - ((left + right) / 2) * zoom, y: 24 - Math.min(...ys) * zoom, zoom },
      { duration: 200 }
    );
    // Only on a new workflow or a resize: an edit must not move the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, width]);

  return (
    <ReactFlow<StepNode, ArrowEdge>
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodeClick={(_, n) => onSelect(n.id)}
      onPaneClick={() => onSelect(null)}
      onConnect={(c) => c.source && c.target && onConnect(c.source, c.target)}
      nodesDraggable={false}
      nodesConnectable={!busy}
      elementsSelectable={false}
      connectionRadius={40}
      minZoom={0.2}
      maxZoom={1.5}
      proOptions={{ hideAttribution: false }}
    >
      <Background gap={20} size={1} />
      <Controls showInteractive={false} position="bottom-left" />
    </ReactFlow>
  );
}

/** The first spoken line of a step, in English, for the box. */
function firstLine(
  step: WorkflowStep | undefined,
  component: AgentComponent | undefined
): string | null {
  if (!step || !component) return null;
  for (const field of component.meta.ui) {
    if (field.kind !== 'locale-text' || field.storage !== 'slot') continue;
    const named = configAt(step.config, field.path);
    const name = typeof named === 'string' ? named : (field.defaultSlot ?? field.path);
    const en = step.content[name]?.en?.trim();
    if (en) return en;
  }
  return null;
}

function StepBox({ data }: NodeProps<StepNode>): React.ReactElement {
  const Icon = data.category ? CATEGORY_ICONS[data.category] : MessageSquare;
  return (
    <div
      className={cn(
        'flex h-26 w-66 flex-col gap-1 overflow-hidden rounded-lg border bg-card px-3 py-2 text-card-foreground shadow-sm transition-shadow',
        data.selected
          ? 'border-primary ring-2 ring-primary/30'
          : 'hover:border-muted-foreground/50',
        data.category === 'decide' && 'border-dashed',
        data.orphan && 'opacity-60'
      )}
    >
      {/* A drop point only: an arrow is drawn from the dot at the bottom. */}
      <Handle type="target" position={Position.Top} isConnectableStart={false} />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{data.kind}</span>
        {data.shared && <Share2 className="h-3 w-3 shrink-0" aria-label="Shared step" />}
        <span className="ml-auto flex items-center gap-1">
          {data.isEntry && (
            <span className="rounded-full bg-primary/10 px-1.5 text-primary">Call starts</span>
          )}
          {data.errors > 0 && (
            <span className="flex items-center gap-0.5 text-destructive">
              <TriangleAlert className="h-3.5 w-3.5" />
              {data.errors}
            </span>
          )}
          {data.errors === 0 && data.warnings > 0 && (
            <TriangleAlert className="h-3.5 w-3.5" aria-label={`${data.warnings} warnings`} />
          )}
        </span>
      </div>
      <div className="truncate text-sm font-medium">{data.name}</div>
      <p className="line-clamp-1 text-xs text-muted-foreground">
        {data.orphan ? 'Nothing leads here yet' : data.detail}
      </p>
      <div className="mt-auto flex gap-1 overflow-hidden text-xs">
        {data.anyTime.map((label) => (
          <span
            key={label}
            className="flex shrink-0 items-center gap-1 rounded-full bg-accent px-1.5 text-accent-foreground"
          >
            <Zap className="h-3 w-3" />
            Any time: {label}
          </span>
        ))}
        {data.endsOn.map((label) => (
          <span
            key={label}
            className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 text-muted-foreground"
          >
            <PhoneOff className="h-3 w-3" />
            {label}: call ends
          </span>
        ))}
        {data.endsCall && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 text-muted-foreground">
            <PhoneOff className="h-3 w-3" />
            Call ends
          </span>
        )}
        {data.legacyGates && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 text-muted-foreground">
            <History className="h-3 w-3" />
            Older conditions
          </span>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

function Arrow({ id, data, markerEnd }: EdgeProps<ArrowEdge>): React.ReactElement | null {
  if (!data) return null;
  return (
    <>
      <BaseEdge id={id} path={data.path} markerEnd={markerEnd} />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan pointer-events-auto absolute flex items-center gap-1"
          // Positioned on the canvas by the layout; no class can express a
          // computed coordinate.
          style={{
            transform: `translate(-50%, -50%) translate(${data.labelAt.x}px, ${data.labelAt.y}px)`,
          }}
        >
          {data.label && (
            <button
              type="button"
              onClick={data.onPick}
              className="max-w-60 truncate rounded-full border bg-background px-2 py-0.5 text-xs font-medium hover:border-primary hover:text-primary"
              title="Change where this goes"
            >
              {data.label}
            </button>
          )}
          <button
            type="button"
            onClick={data.onInsert}
            disabled={data.busy}
            aria-label="Add a step here"
            title="Add a step here"
            className="grid h-5 w-5 place-items-center rounded-full border bg-background text-muted-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
